// NEW: src/components/concept-image-slide.jsx
//
// Full-screen image slide with optional caption. Used to render concept-level
// and card-level `imageUrl` fields from the enriched chapter JSON.
//
// Pure render — own Prev/Next footer (matches concept-table-slide and
// question-bank-slide patterns). The parent (EnhancedLearnMode) hides the
// shared Next/Back nav for this slide type so nav doesn't double up.

import { useState, useEffect } from "react";
import { getFontFamily } from "../constants/colors";

export function ConceptImageSlide({ imageUrl, caption, onNext, onPrev, lang }) {
  const ff = getFontFamily(lang);
  const [loaded, setLoaded] = useState(false);
  const [errored, setErrored] = useState(false);
  // Reset load/error state whenever the image URL changes — otherwise state
  // sticks across slide navigation when the same component instance is re-used.
  useEffect(() => { setLoaded(false); setErrored(false); }, [imageUrl]);
  const hasCaption = typeof caption === "string" && caption.trim().length > 0;

  return (
    <div style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      background: "#FFFFFF",
      fontFamily: ff,
      width: "100%",
      minHeight: 0,
    }}>
      {/* Header strip with caption — omitted when caption is empty */}
      {hasCaption && (
        <div style={{
          padding: "14px 20px",
          borderBottom: "1px solid #EEEEEE",
          background: "#FFFFFF",
          textAlign: "center",
          flexShrink: 0,
        }}>
          <div style={{
            fontSize: 16,
            fontWeight: 600,
            color: "#6AAAC8",
            fontFamily: ff,
            letterSpacing: "-0.01em",
            lineHeight: 1.3,
          }}>
            {caption}
          </div>
        </div>
      )}

      {/* Image area */}
      <div style={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
        overflow: "hidden",
      }}>
        {errored ? (
          <div style={{
            fontSize: 14,
            color: "#999999",
            fontStyle: "italic",
            fontFamily: ff,
            textAlign: "center",
          }}>
            Image not available
          </div>
        ) : (
          <>
            {!loaded && (
              <div style={{
                position: "absolute",
                fontSize: 14,
                color: "#999999",
                fontFamily: ff,
              }}>
                Loading image…
              </div>
            )}
            <img
              src={imageUrl}
              alt={hasCaption ? caption : "Concept illustration"}
              onLoad={() => setLoaded(true)}
              onError={() => setErrored(true)}
              style={{
                maxWidth: "100%",
                maxHeight: "60vh",
                objectFit: "contain",
                background: "#FFFFFF",
                border: "1px solid #EEEEEE",
                borderRadius: 8,
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                display: loaded ? "block" : "none",
              }}
            />
          </>
        )}
      </div>

      {/* Footer nav */}
      <div style={{
        height: 60,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "0 16px",
        borderTop: "1px solid #EEEEEE",
        background: "#FFFFFF",
        flexShrink: 0,
        boxSizing: "border-box",
      }}>
        <button
          onClick={onPrev}
          style={{
            minHeight: 44,
            minWidth: 44,
            padding: "10px 14px",
            background: "transparent",
            border: "none",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: ff,
            color: "#6AAAC8",
            cursor: "pointer",
          }}>
          ‹ Previous
        </button>
        <button
          onClick={onNext}
          style={{
            minHeight: 44,
            minWidth: 44,
            padding: "10px 20px",
            background: "#6AAAC8",
            color: "#FFFFFF",
            border: "none",
            borderRadius: 12,
            fontSize: 14,
            fontWeight: 600,
            fontFamily: ff,
            cursor: "pointer",
            boxShadow: "0 4px 12px rgba(106,170,200,0.3)",
          }}>
          Next ›
        </button>
      </div>
    </div>
  );
}

export default ConceptImageSlide;
